import React from 'react';
import { QueryRenderer } from '@cubejs-client/react';
import { CubejsApi } from '@cubejs-client/core';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { DateTime } from 'luxon';

interface AthleteAppVersionChartProps {
    cubejsApi: CubejsApi;
}

// Color palette for different versions
const VERSION_COLORS = [
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7300',
    '#00C49F',
    '#FFBB28',
    '#FF8042',
    '#0088FE',
    '#00C49F',
    '#FFBB28',
    '#a4de6c',
    '#d0ed57',
    '#ffc658',
    '#ff8042',
    '#8dd1e1',
];

const AthleteAppVersionChart: React.FC<AthleteAppVersionChartProps> = ({ cubejsApi }) => {
    return (
        <QueryRenderer
            cubejsApi={cubejsApi}
            query={{
                measures: ['AthleteAppUsers.uniqueUsers'],
                dimensions: ['AthleteAppUsers.appVersion'],
                timeDimensions: [
                    {
                        dimension: 'AthleteAppUsers.timestamp',
                        granularity: 'day',
                        dateRange: 'Last 30 days',
                    },
                ],
                filters: [
                    {
                        member: 'AthleteAppUsers.appVersion',
                        operator: 'set',
                    },
                ],
                order: {
                    'AthleteAppUsers.timestamp': 'asc',
                },
            }}
            render={({ resultSet, loadingState, error }) => {
                const isLoading = loadingState?.isLoading ?? !resultSet;

                if (isLoading && !error) {
                    return (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight={400}
                        >
                            <CircularProgress sx={{ color: '#a08060' }} />
                        </Box>
                    );
                }

                if (error) {
                    return (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight={400}
                        >
                            <Typography sx={{ color: '#ff6b6b' }}>
                                Error loading chart: {error.toString()}
                            </Typography>
                        </Box>
                    );
                }

                if (!resultSet) {
                    return null;
                }

                const rawData = resultSet.tablePivot();

                if (rawData.length === 0) {
                    return (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight={400}
                            sx={{ color: '#6b5d4f' }}
                        >
                            <Typography variant="h6">No Data Available</Typography>
                        </Box>
                    );
                }

                // Get unique versions and dates
                const versions = [
                    ...new Set(rawData.map((row) => row['AthleteAppUsers.appVersion'] as string)),
                ];
                const dates = [
                    ...new Set(
                        rawData.map((row) => row['AthleteAppUsers.timestamp.day'] as string),
                    ),
                ];

                // Sort versions by total users (descending) to show most popular first
                const versionTotals = versions.map((version) => ({
                    version,
                    total: rawData
                        .filter((row) => row['AthleteAppUsers.appVersion'] === version)
                        .reduce(
                            (sum, row) => sum + Number(row['AthleteAppUsers.uniqueUsers'] || 0),
                            0,
                        ),
                }));
                versionTotals.sort((a, b) => b.total - a.total);
                const sortedVersions = versionTotals.map((v) => v.version);

                // Limit to top 10 versions for readability
                const topVersions = sortedVersions.slice(0, 10);

                // Transform data for Recharts
                const chartData = dates.map((date) => {
                    const dataPoint: Record<string, string | number> = {
                        date: DateTime.fromISO(date).toFormat('MMM dd'),
                        fullDate: date,
                    };

                    topVersions.forEach((version) => {
                        const row = rawData.find(
                            (r) =>
                                r['AthleteAppUsers.timestamp.day'] === date &&
                                r['AthleteAppUsers.appVersion'] === version,
                        );
                        dataPoint[version] = row
                            ? Number(row['AthleteAppUsers.uniqueUsers'] || 0)
                            : 0;
                    });

                    return dataPoint;
                });

                return (
                    <Box>
                        <Box sx={{ height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                    <XAxis
                                        dataKey="date"
                                        stroke="#a0a0a0"
                                        tick={{ fill: '#a0a0a0', fontSize: 11 }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis
                                        stroke="#a0a0a0"
                                        tick={{ fill: '#a0a0a0', fontSize: 12 }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#1a1a1a',
                                            border: '1px solid #4a3f35',
                                            borderRadius: '4px',
                                        }}
                                        labelStyle={{ color: '#fff' }}
                                        labelFormatter={(label, payload) => {
                                            if (payload && payload[0]) {
                                                return payload[0].payload.fullDate?.split('T')[0];
                                            }
                                            return label;
                                        }}
                                    />
                                    <Legend
                                        wrapperStyle={{ color: '#a0a0a0', paddingTop: '20px' }}
                                        formatter={(value) => (
                                            <span style={{ color: '#d0d0d0' }}>v{value}</span>
                                        )}
                                    />
                                    {topVersions.map((version, index) => (
                                        <Line
                                            key={version}
                                            type="monotone"
                                            dataKey={version}
                                            stroke={VERSION_COLORS[index % VERSION_COLORS.length]}
                                            strokeWidth={2}
                                            dot={{ r: 2 }}
                                            activeDot={{ r: 4 }}
                                            name={version}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#6b5d4f' }}>
                                Showing top {topVersions.length} versions by total users (Last 30
                                days)
                            </Typography>
                        </Box>
                    </Box>
                );
            }}
        />
    );
};

export default AthleteAppVersionChart;
